import { Outlet, useMatch } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { PasNovaVerzia } from "@/components/layout/PasNovaVerzia";
import { UserNameDialog } from "@/components/layout/UserNameDialog";
import { ImportProvider } from "@/lib/import/ImportContext";
import { Toaster } from "@/components/ui/toaster";
import { useMe } from "@/api/hooks/useAuth";

/**
 * Aplikačný shell — Header + hlavná content area + Toaster + first-run dialog.
 *
 * Okno stránky **nescrolluje** (`h-screen overflow-hidden`) — scrolluje `main`.
 * Vďaka tomu si editor zákazky vie vypýtať presne zvyšnú výšku a scrollovať
 * len tabuľku dielcov namiesto celého rozhrania (KOLO 56). Header je nad
 * `main`, takže je vždy hore aj bez `sticky`.
 *
 * `min-h-full flex flex-col` na containeri dáva stránke, ktorá o to stojí
 * (editor), zvyšnú výšku cez `flex-1`. Ostatné stránky majú auto-výšku
 * a scrollujú v `main`. Keď sa fixný layout na nízkom okne nezmestí,
 * `overflow-y-auto` na `main` ho nechá odscrollovať — lepšie než stlačiť
 * tabuľku na pár pixelov.
 *
 * **Editor zákazky dostáva plnú šírku okna** (KOLO 99). `container` bez
 * configu je Tailwind default, teda strop 1536 px — pri okne 1920 px
 * odreže 384 px a tabuľka dielcov, ktorá potrebuje ~1336, začne
 * vodorovne scrollovať. Editor je pracovná obrazovka so širokou
 * tabuľkou, takže strop nemá; **zvyšok appky si ho necháva**, aby sa
 * formuláre a dlhé texty neťahali cez celý monitor.
 *
 * Rozhoduje `useMatch("/zakazky/:id")`, nie podreťazec v ceste —
 * `/zakazky` (zoznam) je bežná stránka a chytiť ho nesmie.
 *
 * UserNameDialog (meno pre X-Autor audit hlavičku) sa zobrazuje iba pri
 * vypnutej auth — prihlásený user má meno zo svojho účtu.
 */
export function AppShell(): JSX.Element {
  const { data } = useMe();
  const authDisabled = data?.data.auth_enabled === false;
  const jeEditor = useMatch("/zakazky/:id") !== null;

  return (
    // Sprievodca importu je **nad** stránkou (KOLO 117): dovtedy visel
    // na zozname zákaziek, takže odchod na inú zákazku zahodil aj
    // rozrobený import. Odtiaľto prežije prechod medzi stránkami
    // a minimalizované okno má kam sa vrátiť.
    <ImportProvider>
      <div className="flex h-screen flex-col overflow-hidden bg-background">
      <Header />
      <PasNovaVerzia />
      <main className="min-h-0 flex-1 overflow-y-auto" data-testid="app-main">
        <div
          className={
            jeEditor
              ? "flex min-h-full w-full flex-col px-6 py-4"
              : "container mx-auto flex min-h-full flex-col px-6 py-4"
          }
          data-testid="app-container"
        >
          <Outlet />
        </div>
      </main>
      <Toaster />
      {authDisabled ? <UserNameDialog /> : null}
      </div>
    </ImportProvider>
  );
}
